<template>
  <div class="sso-login-container">
    <div class="loading" v-if="isProcessing">
      <h2>Authenticating...</h2>
      <p>Please wait while we log you in.</p>
    </div>
    <div class="error" v-else-if="error">
      <h2>Authentication Error</h2>
      <p>{{ error }}</p>
      <button @click="redirectToLogin">Go to Login</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { CognitoUserPool, CognitoUser } from 'amazon-cognito-identity-js'
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js'
import { POOL_DATA_COMPANY, POOL_DATA_CLIENT } from '@/env'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

const isProcessing = ref(true)
const error = ref('')

onMounted(async () => {
  try {
    const refreshToken = route.query.refresh_token
    const username = route.query.username
    const userType = route.query.user_type || 'company'

    if (!refreshToken || !username) {
      throw new Error('Missing authentication parameters')
    }

    console.log(
      'SSO Login - Authenticating with refresh token for:',
      username,
      'user_type:',
      userType
    )

    // Clear existing session to allow switching between users
    if (userStore.role && userStore.token) {
      console.log('Clearing existing session to authenticate new user')
      userStore.clearUser()
    }

    await authenticateWithRefreshToken(username, refreshToken, userType)
  } catch (err) {
    console.error('SSO Login Error:', err)
    error.value = err.message || 'Failed to authenticate'
    isProcessing.value = false
  }
})

async function authenticateWithRefreshToken(username, refreshTokenString, userType = 'company') {
  return new Promise((resolve, reject) => {
    try {
      const poolData = userType === 'client' ? POOL_DATA_CLIENT : POOL_DATA_COMPANY
      console.log('Using pool for user type:', userType, poolData.UserPoolId)

      const userPool = new CognitoUserPool(poolData)

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      })

      // Create CognitoRefreshToken object
      const refreshToken = new AmazonCognitoIdentity.CognitoRefreshToken({
        RefreshToken: refreshTokenString,
      })

      console.log('Refreshing session with token...')

      // Refresh the session using the refresh token
      cognitoUser.refreshSession(refreshToken, (err, session) => {
        if (err) {
          console.error('Failed to refresh session:', err)
          error.value = 'Authentication failed. Please reconnect your JAMS account from WordPress.'
          isProcessing.value = false
          reject(err)
          return
        }

        console.log('Session refreshed successfully!', session)

        // Extract tokens and user data from session
        const idToken = session.getIdToken().getJwtToken()
        const accessToken = session.getAccessToken().getJwtToken()
        const refreshTokenNew = session.getRefreshToken().getToken()
        const role = session.getAccessToken().payload['cognito:groups']?.[0]
        const juid = session.getIdToken().payload['custom:juid']
        const isPasswordChanged = session.getIdToken().payload['custom:isPasswordChanged']

        // Update user store with session data
        userStore.setToken(idToken)
        userStore.setRefresh(refreshTokenNew)
        userStore.setRole(role)
        userStore.setJuid(juid)
        userStore.setUsername(username)
        userStore.setIsPasswordChanged(isPasswordChanged)

        // Store tokens in localStorage
        localStorage.setItem('access_token', accessToken)
        localStorage.setItem('id_token', idToken)
        localStorage.setItem('refresh_token', refreshTokenNew)

        console.log('User authenticated successfully via SSO! Role:', role)
        isProcessing.value = false

        // Redirect based on user role
        if (role && role.startsWith('client_')) {
          // Client users go to users page (no name param required)
          router.push({ name: 'UsersViewClient' })
        } else {
          // Company users go to account admin
          router.push({ name: 'Account' })
        }
        resolve(session)
      })
    } catch (err) {
      console.error('Error during authentication:', err)
      error.value = 'Authentication failed. Please try again.'
      isProcessing.value = false
      reject(err)
    }
  })
}

function redirectToLogin() {
  router.push({ name: 'SignInAdmin' })
}
</script>

<style scoped>
.sso-login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.loading,
.error {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 400px;
  width: 100%;
  margin: 0 1rem;
}

.loading h2,
.error h2 {
  margin-bottom: 1rem;
  color: #333;
}

.loading p,
.error p {
  color: #666;
  margin-bottom: 1rem;
}

.error button {
  background: #667eea;
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

.error button:hover {
  background: #5a67d8;
}
</style>
